// Inline 16-grid stroke icons (docs/spec/design-system.md §5: no icon font, no
// icon library — hand-drawn strokes from the revision-6 reference). Every icon
// is decorative-by-default (aria-hidden): the label beside it carries the
// meaning.

import type { JSX } from "react";

function icon(paths: JSX.Element, viewBox = "0 0 16 16") {
  return function Icon() {
    return (
      <svg viewBox={viewBox} aria-hidden="true" focusable="false">
        {paths}
      </svg>
    );
  };
}

export const IconHome = icon(<path d="M2 8 8 2.2 14 8M3.5 7v6.5h3.5v-4h2v4h3.5V7" />);
export const IconCatalogue = icon(
  <path d="M2 5.2 8 2.4l6 2.8v5.6L8 13.6 2 10.8zM2 5.2 8 8m0 0 6-2.8M8 8v5.6" />,
);
export const IconInventory = icon(<path d="M2.5 3h11v3h-11zM2.5 9h11v3.5h-11zM5 4.5h1M5 10.8h1" />);
export const IconProduction = icon(<path d="M9 1.5 3.5 9h3.6l-1 5.5L11.5 7H7.9z" />);
export const IconWaste = icon(
  <path d="M3 4.2h10M6.3 4.2V2.6h3.4v1.6M4.4 4.2l.9 9.2h5.4l.9-9.2M6.8 6.8v4M9.2 6.8v4" />,
);
/** Module 07 — a checked shield: the check is the VERDICT (this was inspected
 * and it held), the shield is what a quality gate does for everything
 * downstream. Deliberately not a magnifier, which is already the search icon,
 * and not a clipboard, which reads as a form rather than as a decision. */
export const IconQuality = icon(
  <path d="M8 1.8 13.2 4v4.1c0 3-2.1 5.2-5.2 6.1-3.1-.9-5.2-3.1-5.2-6.1V4zM5.6 8.1l1.7 1.8 3.1-3.4" />,
);
/** Module 09 — a price tag with its eyelet. The sales area's subject is what a
 * thing COSTS a particular customer: a tag is the one shape that means "this
 * has a price on it", and it collides with nothing else in the strip (the
 * document icon is a quote/order shape and belongs to the slices that ship
 * them). */
export const IconSales = icon(
  <>
    <path d="M8.4 1.9H14v5.6l-6.5 6.5-5.6-5.6z" />
    <circle cx="11.2" cy="4.7" r="1.05" />
  </>,
);
export const IconSetup = icon(
  <>
    <circle cx="8" cy="8" r="2.2" />
    <path d="M8 1.8v2M8 12.2v2M1.8 8h2M12.2 8h2M3.6 3.6l1.4 1.4M11 11l1.4 1.4M12.4 3.6 11 5M5 11l-1.4 1.4" />
  </>,
);
/** Module 17's inbox entry: a speech bubble with its tail — the one shape in
 * the strip that says "somebody wrote to us". It collides with nothing else:
 * the document icon is a quote/order sheet, the sales tag is commerce, and no
 * other entry is a conversation. */
export const IconMessaging = icon(<path d="M2.2 3.4h11.6v7.2H7.4l-3.1 2.6v-2.6H2.2z" />);
/** Module 03: a calendar with weekly columns, the planner's grid of weeks. */
export const IconPlanning = icon(
  <path d="M2.5 3.5h11v10h-11zM2.5 6.5h11M5 2v3M11 2v3M6.2 6.5v7M9.8 6.5v7" />,
);
export const IconFloor = icon(
  <path d="M2 13.5V6l4-2.5V13.5M6 6.8l4-2.3v9M10 7.2l4-1.7v8M2 13.5h12" />,
);
export const IconDocument = icon(<path d="M4 2h5.5L13 5.5V14H4zM9.3 2.2V6H13M6 8.6h4M6 11h4" />);
export const IconSearch = icon(
  <>
    <circle cx="6" cy="6" r="4.4" />
    <path d="m9.4 9.4 3 3" />
  </>,
  "0 0 14 14",
);
export const IconMenu = icon(<path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />);
/** The audit trail: a list with a clock, the record of who did what when. */
/** Module 14 — a spark beside a small panel: work an agent proposes, set
 * apart from the people's own areas. Deliberately not a robot face. */
export const IconAgents = icon(
  <>
    <path d="M2.5 4.5h7v8h-7z" />
    <path d="M12.5 1.8v3M11 3.3h3M12.5 8.2v2.4M11.3 9.4h2.4" />
    <path d="M4.5 7.5h3M4.5 10h2" />
  </>,
);
export const IconAudit = icon(
  <>
    <path d="M2.5 3.5h7M2.5 6.5h5M2.5 9.5h3.5" />
    <circle cx="11" cy="10.5" r="3" />
    <path d="M11 9.2v1.4l.9.7" />
  </>,
);
/** Terminology: a speech tag, the words a factory calls things. */
export const IconTerms = icon(<path d="M2.5 3h11v7.5H8l-3 2.5v-2.5H2.5zM5 6h6M5 8h4" />);
/** The industry pack: a box with its lid. */
export const IconPack = icon(<path d="M2 5.5h12v8H2zM1.5 3h13v2.5h-13zM6.5 8h3" />);
/** The phone bar's "More": three dots. */
export const IconMore = icon(
  <>
    <circle cx="3.5" cy="8" r="0.9" />
    <circle cx="8" cy="8" r="0.9" />
    <circle cx="12.5" cy="8" r="0.9" />
  </>,
);
/** Help: a question mark in a ring. The topbar's link to the customer guide
 * draws it alone at phone width, where the word would crowd the session
 * cluster, so the link carries the word as its accessible name. */
export const IconHelp = icon(
  <>
    <circle cx="8" cy="8" r="6.2" />
    <path d="M6.2 6.3a1.9 1.9 0 1 1 2.6 1.7c-.5.2-.8.6-.8 1.1v.5M8 11.4v.1" />
  </>,
);
export const IconCheckSmall = icon(<path d="m2 5.2 2.1 2.1L8 3.2" />, "0 0 10 10");
export const IconArrowRight = icon(<path d="M3 7h8M8 4l3 3-3 3" />, "0 0 14 14");
export const IconRefresh = icon(<path d="M13 8a5 5 0 1 1-1.5-3.5M13 2.8V5h-2.2" />);
export const IconCaretDown = icon(<path d="m3 4.5 3 3 3-3" />, "0 0 12 12");
export const IconCircleCheck = icon(
  <>
    <circle cx="8" cy="8" r="6.2" />
    <path d="m5.2 8.2 2 2 3.6-4" />
  </>,
);
export const IconAlert = icon(<path d="M8 2 1.8 13h12.4zM8 6.4v3.2M8 11.6v.2" />);

// ---- the platform console's rail (spec 15 §4, D-626) ----------------------
// One mark per job group, drawn now for every group so the M15 lanes that add
// those screens pick a name from console-nav.ts and touch no icon file.
/** Tenants: a building with windows. */
export const IconBuilding = icon(
  <path d="M3 14V2.5h7V14M10 6h3v8M1.5 14h13M5 5h1M7.5 5h1M5 7.5h1M7.5 7.5h1M5 10h1M7.5 10h1" />,
);
/** Support: a lifebuoy. */
export const IconLifebuoy = icon(
  <>
    <circle cx="8" cy="8" r="5.8" />
    <circle cx="8" cy="8" r="2.4" />
    <path d="M3.9 3.9l2.4 2.4M12.1 3.9 9.7 6.3M3.9 12.1l2.4-2.4M12.1 12.1 9.7 9.7" />
  </>,
);
/** Health: a pulse line. */
export const IconPulse = icon(<path d="M1.5 8.5h3l1.5-4 2.5 7 1.8-4.5.9 1.5h3.3" />);
/** Usage: three bars. */
export const IconBars = icon(<path d="M2.5 13.5h11M4 11V7.5M8 11V3.5M12 11V6" />);
/** Staff: two people. */
export const IconPeople = icon(
  <>
    <circle cx="6" cy="5.5" r="2.2" />
    <path d="M2 13c.4-2.4 2-3.7 4-3.7s3.6 1.3 4 3.7" />
    <path d="M10.6 3.6a2 2 0 1 1 .4 3.9M11.6 9.6c1.4.3 2.3 1.5 2.6 3.4" />
  </>,
);
/** Account: one person. */
export const IconPerson = icon(
  <>
    <circle cx="8" cy="5.5" r="2.6" />
    <path d="M3 14c.5-2.9 2.4-4.4 5-4.4s4.5 1.5 5 4.4" />
  </>,
);
/** Sign out: a door with an arrow leaving it. */
export const IconSignOut = icon(<path d="M9.5 2.5h-6v11h6M7 8h7M11.5 5.5 14 8l-2.5 2.5" />);
